import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TextField,
  Snackbar,
  Tabs,
  Tab,
  Autocomplete,
  Paper,
  Avatar,
  Link,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import {
  Send,
  Receipt as ReceiptIcon,
  ReceiptLong as ReceiptLongIcon,
  CloudSync,
  AttachMoney,
  PictureAsPdf,
  Visibility,
  AccountBalanceWalletOutlined,
  ErrorOutlineOutlined,
  SavingsOutlined,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api, { enhancedApi } from '../api';
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
];

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'PENDING', label: 'Draft' },
  { key: 'SENT', label: 'Sent' },
  { key: 'DONE', label: 'Paid' },
  { key: 'OVERDUE', label: 'Overdue' },
  { key: 'VOID', label: 'Void' },
];

const STATUS_CONFIG = {
  PENDING: { label: 'Draft', color: 'default' },
  SENT: { label: 'Sent', color: 'info' },
  OVERDUE: { label: 'Overdue', color: 'error' },
  DONE: { label: 'Paid', color: 'success' },
  VOID: { label: 'Void', color: 'default' },
};

const getEffectiveStatus = (invoice) => {
  let status = invoice.status;
  if (status == null || status === '') status = 'PENDING';
  if (typeof status === 'string') {
    const u = status.toUpperCase();
    if (u === 'DRAFT' || u === 'PENDING') status = 'PENDING';
    else if (u === 'SENT') status = 'SENT';
    else if (u === 'DONE' || u === 'PAID') status = 'DONE';
    else if (u === 'VOID') status = 'VOID';
    else status = u;
  }
  if (
    (status === 'SENT' || status === 'PENDING') &&
    invoice.due_date &&
    dayjs(invoice.due_date, ['MM/DD/YYYY', 'YYYY-MM-DD']).isBefore(dayjs(), 'day')
  ) {
    return 'OVERDUE';
  }
  return status;
};

const formatAmount = (val) =>
  `$${parseFloat(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const formatDisplayDate = (val) => {
  if (!val) return '—';
  const d = dayjs(val, ['MM/DD/YYYY', 'YYYY-MM-DD']);
  return d.isValid() ? d.format('MMM D, YYYY') : val;
};

const StatCard = ({ label, amount, borderColor, icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      borderLeftWidth: 4,
      borderLeftStyle: 'solid',
      borderLeftColor: borderColor,
      bgcolor: 'background.paper',
      height: '100%',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Box sx={{ color: borderColor, display: 'flex', opacity: 0.9 }}>{icon}</Box>
    </Box>
    <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
      {amount}
    </Typography>
  </Paper>
);

const BillingInvoices = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [statusTab, setStatusTab] = useState('all');
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [clientFilter, setClientFilter] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [qbSyncLoading, setQbSyncLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [markPaidLoading, setMarkPaidLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/billing/all-invoices/');
      setInvoices(response.data?.invoices || []);
    } catch {
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const enrichedInvoices = useMemo(() => {
    return invoices.map((inv) => ({
      ...inv,
      effectiveStatus: getEffectiveStatus(inv),
    }));
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    let list = enrichedInvoices;
    if (statusTab !== 'all') {
      list = list.filter((inv) => inv.effectiveStatus === statusTab);
    }
    if (dateRangeStart) {
      list = list.filter((inv) => {
        const d = inv.invoice_date ? dayjs(inv.invoice_date, ['MM/DD/YYYY', 'YYYY-MM-DD']) : null;
        return d && d.isValid() && !d.isBefore(dateRangeStart, 'day');
      });
    }
    if (dateRangeEnd) {
      list = list.filter((inv) => {
        const d = inv.invoice_date ? dayjs(inv.invoice_date, ['MM/DD/YYYY', 'YYYY-MM-DD']) : null;
        return d && d.isValid() && !d.isAfter(dateRangeEnd, 'day');
      });
    }
    if (clientFilter) {
      list = list.filter((inv) => inv.client_name === clientFilter);
    }
    return list;
  }, [enrichedInvoices, statusTab, dateRangeStart, dateRangeEnd, clientFilter]);

  const stats = useMemo(() => {
    const now = dayjs();
    const thisMonthStart = now.startOf('month');
    const outstanding = enrichedInvoices.filter(
      (inv) => inv.effectiveStatus !== 'DONE' && inv.effectiveStatus !== 'VOID'
    );
    const overdue = enrichedInvoices.filter((inv) => inv.effectiveStatus === 'OVERDUE');
    const paidThisMonth = enrichedInvoices.filter((inv) => {
      if (inv.effectiveStatus !== 'DONE') return false;
      const paidAt = inv.paid_at || inv.updated_at || inv.invoice_date;
      return paidAt && dayjs(paidAt).isAfter(thisMonthStart);
    });
    return {
      totalOutstanding: outstanding.reduce((s, inv) => s + parseFloat(inv.total || 0), 0),
      totalOverdue: overdue.reduce((s, inv) => s + parseFloat(inv.total || 0), 0),
      paidThisMonth: paidThisMonth.reduce((s, inv) => s + parseFloat(inv.total || 0), 0),
    };
  }, [enrichedInvoices]);

  const clientOptions = useMemo(
    () => Array.from(new Set(enrichedInvoices.map((inv) => inv.client_name).filter(Boolean))).sort(),
    [enrichedInvoices]
  );

  const tabCounts = useMemo(() => {
    const counts = {
      all: enrichedInvoices.length,
      PENDING: 0,
      SENT: 0,
      DONE: 0,
      OVERDUE: 0,
      VOID: 0,
    };
    enrichedInvoices.forEach((inv) => {
      const k = inv.effectiveStatus;
      if (k in counts) counts[k] += 1;
    });
    return counts;
  }, [enrichedInvoices]);

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
    setDetailLoading(true);
    setInvoiceDetail(null);
    try {
      const response = await api.get(`/api/billing/invoice-detail/${invoice.id}/`);
      setInvoiceDetail(response.data);
    } catch {
      setInvoiceDetail({ error: 'Failed to load invoice' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await api.get(`/api/billing/invoice-detail/${invoice.id}/`);
      const pdf = response.data?.pdf;
      if (!pdf) {
        showSnackbar('PDF not available', 'error');
        return;
      }
      const blob = new Blob(
        [Uint8Array.from(atob(pdf), (c) => c.charCodeAt(0))],
        { type: 'application/pdf' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoice_number || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showSnackbar('PDF downloaded');
    } catch {
      showSnackbar('Failed to download PDF', 'error');
    }
  };

  const handleSyncToQuickBooks = async () => {
    if (!selectedInvoice?.id) return;
    setQbSyncLoading(true);
    try {
      const res = await enhancedApi.syncInvoiceToQuickBooks(selectedInvoice.id);
      if (res.data?.success) {
        showSnackbar('Invoice synced to QuickBooks');
      } else {
        showSnackbar(res.data?.error || 'Sync failed', 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to sync', 'error');
    } finally {
      setQbSyncLoading(false);
    }
  };

  const handleSendInvoice = async (invoice) => {
    setSendingId(invoice.id);
    try {
      const res = await api.post(`/api/multi-tenant/invoices/${invoice.id}/send/`);
      showSnackbar(`Invoice sent to ${res.data.sent_to}`);
      fetchInvoices();
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to send invoice', 'error');
    } finally {
      setSendingId(null);
    }
  };

  const handleMarkPaidConfirm = async () => {
    if (!markPaidInvoice) return;
    setMarkPaidLoading(true);
    try {
      await api.post(`/api/multi-tenant/invoices/${markPaidInvoice.id}/mark-paid/`, {
        payment_method: paymentMethod,
      });
      showSnackbar('Invoice marked as paid');
      setMarkPaidInvoice(null);
      fetchInvoices();
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to mark as paid', 'error');
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const handleBulkSend = async () => {
    const toSend = selectedIds.filter((id) => {
      const inv = filteredInvoices.find((i) => i.id === id);
      return inv && inv.effectiveStatus !== 'DONE' && inv.effectiveStatus !== 'VOID';
    });
    if (toSend.length === 0) {
      showSnackbar('No invoices selected to send', 'warning');
      return;
    }
    setBulkLoading(true);
    let sent = 0;
    for (const id of toSend) {
      try {
        await api.post(`/api/multi-tenant/invoices/${id}/send/`);
        sent++;
      } catch {
        // skip failed
      }
    }
    setBulkLoading(false);
    setSelectedIds([]);
    fetchInvoices();
    showSnackbar(`Sent ${sent} of ${toSend.length} invoice(s)`);
  };

  const handleBulkMarkPaid = async () => {
    const toMark = selectedIds.filter((id) => {
      const inv = filteredInvoices.find((i) => i.id === id);
      return inv && inv.effectiveStatus !== 'DONE' && inv.effectiveStatus !== 'VOID';
    });
    if (toMark.length === 0) {
      showSnackbar('No invoices selected to mark as paid', 'warning');
      return;
    }
    setBulkLoading(true);
    let marked = 0;
    for (const id of toMark) {
      try {
        await api.post(`/api/multi-tenant/invoices/${id}/mark-paid/`, { payment_method: 'other' });
        marked++;
      } catch {
        // skip failed
      }
    }
    setBulkLoading(false);
    setSelectedIds([]);
    fetchInvoices();
    showSnackbar(`Marked ${marked} invoice(s) as paid`);
  };

  const handleExport = (ids = null) => {
    const toExport = ids
      ? filteredInvoices.filter((inv) => ids.includes(inv.id))
      : filteredInvoices;
    const headers = ['Invoice #', 'Client', 'Amount', 'Status', 'Issue Date', 'Due Date'];
    const rows = toExport.map((inv) => [
      inv.invoice_number,
      inv.client_name,
      inv.total,
      STATUS_CONFIG[inv.effectiveStatus]?.label || inv.effectiveStatus,
      inv.invoice_date || '',
      inv.due_date || '',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showSnackbar('Exported to CSV');
  };

  const columns = [
    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      flex: 0.75,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const name = params.value || '—';
        const cid = params.row.client_id;
        const initial = String(name).trim().charAt(0).toUpperCase() || '?';
        return (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5, minWidth: 0 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                fontWeight: 700,
                bgcolor: 'primary.light',
                color: 'primary.main',
              }}
            >
              {initial}
            </Avatar>
            {cid ? (
              <Link
                component={RouterLink}
                to={`/client/${cid}`}
                variant="body2"
                fontWeight={600}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {name}
              </Link>
            ) : (
              <Typography variant="body2" fontWeight={600} noWrap>
                {name}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'total',
      headerName: 'Amount',
      flex: 0.75,
      minWidth: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {formatAmount(params.value)}
        </Typography>
      ),
    },
    {
      field: 'effectiveStatus',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => {
        const cfg = STATUS_CONFIG[params.value] || STATUS_CONFIG.PENDING;
        return <Chip label={cfg.label} color={cfg.color} size="small" variant={cfg.color === 'default' ? 'outlined' : 'filled'} />;
      },
    },
    {
      field: 'invoice_date',
      headerName: 'Issue Date',
      flex: 0.85,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatDisplayDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      flex: 0.85,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatDisplayDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.5,
      minWidth: 340,
      sortable: false,
      renderCell: (params) => {
        const inv = params.row;
        const isVoid = inv.effectiveStatus === 'VOID';
        const isSending = sendingId === inv.id;
        const canTransact = inv.effectiveStatus !== 'DONE' && !isVoid;
        return (
          <Box
            className="invoice-row-actions"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              height: '100%',
              opacity: 0,
              transition: 'opacity 0.12s ease-out',
              '.MuiDataGrid-row:hover &': { opacity: 1 },
            }}
          >
            <Tooltip title="View">
              <IconButton size="small" color="inherit" onClick={() => handleViewInvoice(inv)} aria-label="View invoice">
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {canTransact && (
              <Tooltip title="Send">
                <span>
                  <IconButton
                    size="small"
                    color="inherit"
                    disabled={isSending}
                    onClick={() => handleSendInvoice(inv)}
                    aria-label="Send invoice"
                  >
                    {isSending ? <CircularProgress size={18} color="inherit" /> : <Send fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canTransact && (
              <Tooltip title="Record Payment">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setMarkPaidInvoice(inv);
                    setPaymentMethod('cash');
                  }}
                  aria-label="Record payment"
                >
                  <AttachMoney fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Download PDF">
              <IconButton size="small" color="inherit" onClick={() => handleDownloadPdf(inv)} aria-label="Download PDF">
                <PictureAsPdf fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const getRowClassName = (params) =>
    params.row.effectiveStatus === 'OVERDUE' ? 'invoices-row--overdue' : '';

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Skeleton variant="text" width={200} height={36} />
          <Skeleton variant="rounded" width={180} height={40} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
        <Paper
          elevation={0}
          sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}
        >
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 0 }} />
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Skeleton variant="rounded" width={160} height={40} />
              <Skeleton variant="rounded" width={160} height={40} />
              <Skeleton variant="rounded" width={220} height={40} />
            </Stack>
          </Box>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Skeleton variant="rounded" width="100%" height={40} sx={{ mb: 1 }} />
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Skeleton key={i} variant="rounded" width="100%" height={52} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const hasAnyInvoices = enrichedInvoices.length > 0;
  const showFullEmpty = !hasAnyInvoices;

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          Invoices
        </Typography>
        <Button variant="contained" color="primary" startIcon={<ReceiptIcon />} onClick={() => navigate('/billing/invoices/create')}>
          Create Invoice
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          label="Outstanding"
          amount={formatAmount(stats.totalOutstanding)}
          borderColor="warning.main"
          icon={<AccountBalanceWalletOutlined fontSize="small" />}
        />
        <StatCard
          label="Overdue"
          amount={formatAmount(stats.totalOverdue)}
          borderColor="error.main"
          icon={<ErrorOutlineOutlined fontSize="small" />}
        />
        <StatCard
          label="Collected This Month"
          amount={formatAmount(stats.paidThisMonth)}
          borderColor="success.main"
          icon={<SavingsOutlined fontSize="small" />}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={statusTab}
          onChange={(_, v) => setStatusTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            sx: {
              height: 3,
              borderRadius: '3px 3px 0 0',
              bgcolor: 'primary.main',
            },
          }}
          sx={{
            px: 1,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 48,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
              color: 'text.secondary',
              '&.Mui-selected': { color: 'primary.main' },
            },
          }}
        >
          {STATUS_TABS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              disableRipple
              label={
                <Stack component="span" direction="row" alignItems="center" spacing={1}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                    {t.label}
                  </Typography>
                  <Chip
                    component="span"
                    label={tabCounts[t.key] ?? 0}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 22,
                      minWidth: 22,
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                      '& .MuiChip-label': { px: 0.75, fontSize: '0.75rem', fontWeight: 700 },
                    }}
                  />
                </Stack>
              }
            />
          ))}
        </Tabs>

        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" alignItems={{ sm: 'center' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={dateRangeStart}
                onChange={setDateRangeStart}
                slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 160 } } } }}
              />
              <DatePicker
                label="To"
                value={dateRangeEnd}
                onChange={setDateRangeEnd}
                slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 160 } } } }}
              />
            </LocalizationProvider>
            <Autocomplete
              options={clientOptions}
              value={clientFilter}
              onChange={(_, v) => setClientFilter(v)}
              getOptionLabel={(o) => (typeof o === 'string' ? o : '')}
              sx={{ width: { xs: '100%', sm: 220 } }}
              renderInput={(params) => <TextField {...params} label="Client" size="small" />}
            />
          </Stack>
        </Box>

        {hasAnyInvoices && filteredInvoices.length > 0 && selectedIds.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
              {selectedIds.length} invoice{selectedIds.length === 1 ? '' : 's'} selected
            </Typography>
            <Button size="small" variant="outlined" color="primary" onClick={handleBulkSend} disabled={bulkLoading}>
              Send
            </Button>
            <Button size="small" variant="outlined" color="inherit" onClick={() => handleExport(selectedIds)} disabled={bulkLoading}>
              Export
            </Button>
            <Button size="small" variant="outlined" color="success" onClick={handleBulkMarkPaid} disabled={bulkLoading}>
              Mark as Paid
            </Button>
          </Box>
        )}

        {showFullEmpty ? (
          <Box sx={{ py: 10, px: 2, textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No invoices yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first invoice to see it here.
            </Typography>
            <Button variant="contained" color="primary" startIcon={<ReceiptIcon />} onClick={() => navigate('/billing/invoices/create')}>
              Create Invoice
            </Button>
          </Box>
        ) : filteredInvoices.length === 0 ? (
          <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No invoices match your filters or tab selection.
            </Typography>
            <Button size="small" onClick={() => { setStatusTab('all'); setDateRangeStart(null); setDateRangeEnd(null); setClientFilter(null); }}>
              Clear filters
            </Button>
          </Box>
        ) : (
          <Box
            sx={(theme) => {
              const err50 =
                theme.palette.error[50] !== undefined
                  ? theme.palette.error[50]
                  : alpha(theme.palette.error.main, theme.palette.mode === 'light' ? 0.06 : 0.14);
              const grey50 = theme.palette.grey[50];
              return {
                width: '100%',
                '& .invoices-row--overdue': {
                  bgcolor: err50,
                },
                '& .MuiDataGrid-row:not(.invoices-row--overdue):hover': {
                  bgcolor: grey50,
                },
                '& .MuiDataGrid-row.invoices-row--overdue:hover': {
                  bgcolor:
                    theme.palette.error[100] !== undefined
                      ? theme.palette.error[100]
                      : alpha(theme.palette.error.main, theme.palette.mode === 'light' ? 0.1 : 0.2),
                },
                '& .MuiDataGrid-cell': { py: 1 },
                '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' },
              };
            }}
          >
            <DataGrid
              rows={filteredInvoices}
              columns={columns}
              getRowId={(row) => row.id}
              checkboxSelection
              onRowSelectionModelChange={(ids) => setSelectedIds(ids)}
              rowSelectionModel={selectedIds}
              getRowClassName={getRowClassName}
              disableRowSelectionOnClick
              autoHeight
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-footerContainer': { borderTop: 1, borderColor: 'divider' },
              }}
            />
          </Box>
        )}
      </Paper>

      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedInvoice ? `Invoice #${selectedInvoice.invoice_number}` : 'Invoice'}
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : invoiceDetail?.error ? (
            <Alert severity="error">{invoiceDetail.error}</Alert>
          ) : invoiceDetail?.pdf ? (
            <Box sx={{ height: 500 }}>
              <iframe
                title="Invoice PDF"
                src={`data:application/pdf;base64,${invoiceDetail.pdf}`}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<CloudSync />}
            onClick={handleSyncToQuickBooks}
            disabled={qbSyncLoading || !selectedInvoice}
          >
            {qbSyncLoading ? 'Syncing...' : 'Sync to QuickBooks'}
          </Button>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!markPaidInvoice} onClose={() => setMarkPaidInvoice(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Record a manual payment for Invoice #{markPaidInvoice?.invoice_number}
          </Typography>
          <TextField
            label="Payment Method"
            select
            fullWidth
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            size="small"
          >
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidInvoice(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={markPaidLoading}
            startIcon={markPaidLoading ? <CircularProgress size={16} /> : <AttachMoney />}
            onClick={handleMarkPaidConfirm}
          >
            {markPaidLoading ? 'Saving…' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillingInvoices;
